// ============================================================
// Tarification des modèles IA (par million de tokens, en USD).
// Sert à estimer le coût réel de chaque appel enregistré dans
// `zifek_api_logs`. Les valeurs sont des ordres de grandeur
// publics ; elles peuvent être ajustées sans casser l'app.
// ============================================================

export interface ModelPrice {
  /** Prix par 1M de tokens d'entrée (prompt). */
  input: number;
  /** Prix par 1M de tokens de sortie (completion). */
  output: number;
}

/** Prix par défaut si le modèle n'est pas connu. */
export const DEFAULT_PRICE: ModelPrice = { input: 3, output: 12 };

/** Table de tarification par provider puis modèle. */
const PRICE_TABLE: Record<string, Record<string, ModelPrice>> = {
  openai: {
    'gpt-4o': { input: 2.5, output: 10 },
    'gpt-4o-mini': { input: 0.15, output: 0.6 },
    'gpt-4-turbo': { input: 10, output: 30 },
    'gpt-4': { input: 30, output: 60 },
    'gpt-3.5-turbo': { input: 0.5, output: 1.5 },
    'gpt-4.1': { input: 2, output: 8 },
    'gpt-4.1-mini': { input: 0.4, output: 1.6 },
  },
  xai: {
    'grok-4.6': { input: 3, output: 15 },
    'grok-4.5': { input: 3, output: 15 },
    'grok-4.3': { input: 3, output: 15 },
    'grok-4': { input: 3, output: 15 },
    'grok-3': { input: 2, output: 10 },
    'grok-2': { input: 2, output: 10 },
  },
  openrouter: {
    'anthropic/claude-3.5-sonnet': { input: 3, output: 15 },
    'anthropic/claude-3.5-haiku': { input: 0.8, output: 4 },
    'google/gemini-2.0-flash': { input: 0.1, output: 0.4 },
    'openai/gpt-4o': { input: 2.5, output: 10 },
    'openai/gpt-4o-mini': { input: 0.15, output: 0.6 },
    'meta-llama/llama-3.3-70b-instruct': { input: 0.12, output: 0.3 },
    'deepseek/deepseek-chat': { input: 0.14, output: 0.28 },
  },
  ollama: {
    '*': { input: 0, output: 0 }, // auto-hébergé : coût marginal nul
  },
};

/** Retourne le prix (par 1M tokens) d'un provider/modèle. */
export function getModelPrice(provider: string, model: string): ModelPrice {
  const p = (provider || '').toLowerCase();
  const m = (model || '').toLowerCase();
  const byProvider = PRICE_TABLE[p];
  if (!byProvider) return DEFAULT_PRICE;
  if (byProvider[m]) return byProvider[m];
  if (byProvider['*']) return byProvider['*'];
  // Essaye un match partiel (ex. modèle suffixé d'une date)
  const key = Object.keys(byProvider).find((k) => k !== '*' && (m.startsWith(k) || k.startsWith(m)));
  if (key) return byProvider[key];
  return DEFAULT_PRICE;
}

/** Estime le coût en USD d'un appel donné. */
export function estimateCostUsd(
  provider: string,
  model: string,
  requestTokens: number,
  responseTokens: number,
): number {
  const price = getModelPrice(provider, model);
  const inCost = ((requestTokens || 0) / 1_000_000) * price.input;
  const outCost = ((responseTokens || 0) / 1_000_000) * price.output;
  return inCost + outCost;
}

/** Formate un coût en dollars (jusqu'à 4 décimales pour les petits montants). */
export function formatUsd(value: number): string {
  if (!value || value <= 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

/** Liste des providers connus avec leur libellé. */
export const KNOWN_PROVIDERS: { id: string; label: string; icon: string }[] = [
  { id: 'openai', label: 'OpenAI', icon: 'ri-openai-line' },
  { id: 'xai', label: 'xAI Grok', icon: 'ri-brain-line' },
  { id: 'openrouter', label: 'OpenRouter', icon: 'ri-router-line' },
  { id: 'ollama', label: 'Ollama', icon: 'ri-computer-line' },
];